import { type Dispatch, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { Card } from '@/components/atoms/Card';
import { Overline } from '@/components/atoms/Eyebrow';
import FormField from '@/components/atoms/FormField';
import TextArea from '@/components/atoms/TextArea';
import TextField from '@/components/atoms/TextField';
import EmptyState from '@/components/molecules/EmptyState';
import { AllocationSummary, SuggestNameButton } from '@/components/organisms/FishGroupForms';
import type { FishGroupPopulationInfo, OverviewCage } from '@/domain/fishGroups';

import { computeAllocationSummary, type WizardValidationError } from '../../operateWizardHelpers';
import type {
    OperateWizardAction,
    WizardDestination,
    WizardSource,
} from '../../operateWizardReducer';
import { ValidationBanner } from '../shared/ValidationBanner';

import styles from '../../operateModals.module.scss';

interface Props {
    initialSource: WizardSource;
    /** All cages of the location — used to resolve the source cage name. */
    locationCages: OverviewCage[];
    /**
     * `destinations[1]` — the source continuation. Auto-created on mount via
     * `addSplitRemainder`. `null` only on the first render before the effect
     * has fired, or if the source's current cage is unknown.
     */
    continuation: WizardDestination | null;
    errors: WizardValidationError[];
    sources: WizardSource[];
    destinations: WizardDestination[];
    populationById: Map<number, FishGroupPopulationInfo>;
    dispatch: Dispatch<OperateWizardAction>;
}

/**
 * Split-and-merge mode — Step 2. The source fish group has been split: part
 * of its population merges into the target cage (configured in
 * MergeTargetStep, stored at `destinations[0]`); the remainder stays in the
 * source's own cage as a brand-new FG.
 *
 * Unlike the previous design — which let the operator place the remainder in
 * an arbitrary empty cage — the continuation is now *pinned* to the source
 * cage by definition. Moving the remainder elsewhere is what
 * `redistribution` is for; here we lock the cage so the common, guided
 * "absorb a neighbour and keep going" flow is unambiguous.
 *
 * The continuation is created on mount via `addSplitRemainder` (idempotent),
 * which also flips the inferred operation type from a degenerate merge
 * (S=2, D=1) to a true split-and-merge (S=2, D=2).
 */
export default function SourceContinuationStep(props: Props) {
    const {
        initialSource,
        locationCages,
        continuation,
        errors,
        sources,
        destinations,
        populationById,
        dispatch,
    } = props;
    const { t } = useTranslation('fishGroups');

    const sourceCageId = initialSource.currentCageId;
    const sourceCageName =
        sourceCageId != null
            ? (locationCages.find((c) => c.cageId === sourceCageId)?.cageName ?? '')
            : '';

    // Auto-add the continuation on first mount so the operator lands on the
    // guided "source stays here, you only name it and estimate the count"
    // configuration. Idempotent: noop if the continuation already exists,
    // or if the source cage is unknown (defensive — shouldn't happen for an
    // active FG).
    // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally mount-only
    useEffect(() => {
        if (sourceCageId == null) return;
        dispatch({ type: 'addSplitRemainder' });
    }, []);

    const allocationSummary = computeAllocationSummary({
        sources,
        destinations,
        populationById,
    });

    return (
        <section className={styles.body}>
            <div>
                <h4 className={styles.heading}>
                    {t('actions.operate.splitAndMerge.continuation.heading')}
                </h4>
                <p className={styles.description}>
                    {t('actions.operate.splitAndMerge.continuation.description', {
                        cageName: sourceCageName,
                    })}
                </p>
            </div>

            <Card radius="sm" variant="subtle" padding="none" className={styles.contextCard}>
                <Overline>{t('actions.operate.splitAndMerge.sourceLabel')}</Overline>
                <span className={styles.contextValue}>
                    {initialSource.name}
                    {sourceCageName && (
                        <span className={styles.summaryMeta}> ({sourceCageName})</span>
                    )}
                </span>
            </Card>

            <AllocationSummary
                summary={allocationSummary}
                sources={sources}
                populationById={populationById}
                titleKey="actions.operate.allocation.combinedSourcesTitle"
            />

            {errors.length > 0 && <ValidationBanner errors={errors} />}

            {continuation ? (
                <ContinuationCard
                    destination={continuation}
                    sourceCageName={sourceCageName}
                    sourceName={initialSource.name}
                    dispatch={dispatch}
                />
            ) : (
                <EmptyState
                    title={t('actions.operate.splitAndMerge.continuation.unavailable')}
                    appearance="quiet"
                />
            )}
        </section>
    );
}

interface ContinuationCardProps {
    destination: WizardDestination;
    sourceCageName: string;
    sourceName: string;
    dispatch: Dispatch<OperateWizardAction>;
}

function ContinuationCard(props: ContinuationCardProps) {
    const { destination, sourceCageName, sourceName, dispatch } = props;
    const { t } = useTranslation('fishGroups');

    return (
        <Card
            as="article"
            radius="sm"
            padding="xs"
            accent="stripe"
            className={classNames(styles.destinationCard, styles.remainderCard)}>
            <header className={styles.destinationHeader}>
                <h5 className={styles.destinationTitle}>
                    {t('actions.operate.splitAndMerge.continuation.cardTitle', {
                        cageName: sourceCageName,
                    })}
                </h5>
            </header>

            <div className={styles.fieldsGrid}>
                <FormField
                    size="sm"
                    htmlFor={`${destination.tempId}-name`}
                    label={t('actions.operate.destinations.fields.name')}
                    labelAction={
                        <SuggestNameButton
                            cageName={sourceCageName}
                            operation="split_and_merge"
                            onApply={(value) =>
                                dispatch({
                                    type: 'setDestinationName',
                                    tempId: destination.tempId,
                                    value,
                                })
                            }
                        />
                    }
                    className={styles['field--fullWidth']}>
                    <TextField
                        id={`${destination.tempId}-name`}
                        type="text"
                        placeholder={t(
                            'actions.operate.splitAndMerge.continuation.namePlaceholder',
                            { sourceName }
                        )}
                        value={destination.name}
                        onChange={(e) =>
                            dispatch({
                                type: 'setDestinationName',
                                tempId: destination.tempId,
                                value: e.target.value,
                            })
                        }
                    />
                </FormField>

                <FormField
                    size="sm"
                    htmlFor={`${destination.tempId}-count`}
                    label={t('actions.operate.destinations.fields.estimatedCount')}
                    hint={t('actions.operate.destinations.fields.estimatedCountHint')}>
                    <TextField
                        id={`${destination.tempId}-count`}
                        type="number"
                        min={1}
                        value={destination.estimatedFishCount}
                        onChange={(e) =>
                            dispatch({
                                type: 'setDestinationEstimatedCount',
                                tempId: destination.tempId,
                                value: e.target.value,
                            })
                        }
                    />
                </FormField>

                <FormField
                    size="sm"
                    htmlFor={`${destination.tempId}-notes`}
                    label={t('actions.operate.destinations.fields.notes')}
                    className={styles['field--fullWidth']}>
                    <TextArea
                        id={`${destination.tempId}-notes`}
                        rows={2}
                        value={destination.notes}
                        onChange={(e) =>
                            dispatch({
                                type: 'setDestinationNotes',
                                tempId: destination.tempId,
                                value: e.target.value,
                            })
                        }
                    />
                </FormField>
            </div>
        </Card>
    );
}
